import { VAZIRMATN_REGULAR_BASE64, VAZIRMATN_BOLD_BASE64 } from "./fonts/vazirmatnBase64";

export const ARABIC_FONT_NAME = "Vazirmatn";

// Matches Arabic, Arabic Supplement, Arabic Extended-A, and the presentation-form blocks.
const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

export function containsArabic(text) {
  return ARABIC_RE.test(text || "");
}

// Registers the embedded Arabic font (Vazirmatn) on a jsPDF document instance.
// Safe to call multiple times per doc - it only registers once.
export function registerArabicFont(doc) {
  if (doc.__arabicFontRegistered) return;
  doc.addFileToVFS("Vazirmatn-Regular.ttf", VAZIRMATN_REGULAR_BASE64);
  doc.addFont("Vazirmatn-Regular.ttf", ARABIC_FONT_NAME, "normal");
  doc.addFileToVFS("Vazirmatn-Bold.ttf", VAZIRMATN_BOLD_BASE64);
  doc.addFont("Vazirmatn-Bold.ttf", ARABIC_FONT_NAME, "bold");
  doc.__arabicFontRegistered = true;
}

// NOTE: text is intentionally passed to jsPDF's doc.text() completely raw (in
// logical/typed order) for Arabic. jsPDF/the PDF viewer's text layout already
// performs Arabic contextual shaping and bidi visual reordering from the plain
// Unicode string - pre-reshaping (arabic-reshaper) and pre-reordering (bidi-js)
// was tried and made output *worse* (double-shaped/double-reversed, unreadable).
// Confirmed by direct PDF-output comparison; do not reintroduce that step.

// Draws a block of plain text (already split on \n for paragraphs) into a jsPDF doc,
// switching font/alignment per paragraph based on whether it contains Arabic.
// Wraps each paragraph to maxWidth, calling onBeforeLine(doc, y) before every line
// so callers can paginate exactly like the previous LTR-only drawing loop did.
// Matches a leading list marker ("1. ", "2) ", "• ", "- ") so it can be drawn
// separately from the text it introduces. Needed because a raw marker+Arabic
// string is one bidi run: with the paragraph direction forced RTL by the
// Arabic text, the (bidi-neutral) marker gets reordered to the visual left
// end along with everything else, instead of staying on the right where an
// RTL list marker belongs.
const LIST_MARKER_RE = /^(\s*(?:[••*-]|\d+[.)])\s+)(\S.*)$/;

// Matches an inline "label: value" split (first colon in the line). Same root
// cause as the list marker above: a colon is bidi-neutral, so inside a line
// that is otherwise one RTL run it can get pulled toward the wrong end instead
// of staying stuck to the label it follows. Keeping the colon attached to the
// label segment and drawing the value as its own right-aligned segment avoids
// relying on the renderer to resolve that neutral correctly.
const LABEL_COLON_RE = /^(\s*[^:]*\S\s*:)\s*(\S.*)$/;

export function drawBidiParagraphs(doc, text, opts) {
  const {
    x,
    maxWidth,
    lineHeight,
    fontSize,
    startY,
    onBeforeLine, // (doc, y) => number  -- return possibly-updated y (handles page breaks)
  } = opts;

  registerArabicFont(doc);

  const paragraphs = (text || "").split("\n");
  let y = startY;

  for (const paragraph of paragraphs) {
    const isArabic = containsArabic(paragraph);
    doc.setFontSize(fontSize);
    doc.setFont(isArabic ? ARABIC_FONT_NAME : "helvetica", "normal");

    if (!paragraph) {
      y = onBeforeLine(doc, y);
      y += lineHeight;
      continue;
    }

    const wrapped = doc.splitTextToSize(paragraph, maxWidth);
    for (const line of wrapped) {
      y = onBeforeLine(doc, y);
      if (isArabic) {
        const markerMatch = line.match(LIST_MARKER_RE);
        const colonMatch = !markerMatch && line.match(LABEL_COLON_RE);
        if (markerMatch) {
          const [, marker, rest] = markerMatch;
          doc.text(marker.trim(), x + maxWidth, y, { align: "right" });
          const markerWidth = doc.getTextWidth(marker.trim());
          doc.text(rest, x + maxWidth - markerWidth - 2, y, { align: "right" });
        } else if (colonMatch) {
          const [, label, value] = colonMatch;
          doc.text(label.trim(), x + maxWidth, y, { align: "right" });
          const labelWidth = doc.getTextWidth(label.trim());
          doc.text(value, x + maxWidth - labelWidth - 2, y, { align: "right" });
        } else {
          doc.text(line, x + maxWidth, y, { align: "right" });
        }
      } else {
        doc.text(line, x, y);
      }
      y += lineHeight;
    }
  }

  return y;
}

// Draws a single line of text, auto-switching to the Arabic font when the text
// contains Arabic. Defaults to left alignment so inline "Label: value" layouts
// (value drawn at a fixed x right after a label) keep working exactly like plain
// Latin text - text is drawn raw and shaped/reordered by the PDF viewer itself.
export function drawBidiText(doc, text, x, y, opts = {}) {
  const { fontSize, bold = false, align = "left", ...textOptions } = opts;
  registerArabicFont(doc);
  const isArabic = containsArabic(text);
  if (fontSize) doc.setFontSize(fontSize);
  doc.setFont(isArabic ? ARABIC_FONT_NAME : "helvetica", bold ? "bold" : "normal");
  doc.text(text, x, y, { align, ...textOptions });
}
